import { useEffect } from 'react';
import { logExceptionOnce, logUnknownErrorResponse } from '@hungryroot/o11y';
import { postApiV2CustomerExperimentExposure } from '@hungryroot/rest';

/**
 * Statsig exposure logging for Food Profile (Exp 195 + Exp 265 A/A).
 * Fire once per browser tab session when any Food Profile surface mounts —
 * control and variants both count. Failures must not block the page.
 */
const FOOD_PROFILE_EXPOSURE_EXPERIMENT_REFS = ['exp_195', 'exp_265'] as const;

const SESSION_STORAGE_KEY = 'food-profile-experiment-exposures';

/** In-memory fallback when sessionStorage is unavailable. */
const loggedFoodProfileExperimentExposures = new Set<string>();

/**
 * Fire-and-forget exposure posts (session-deduped). Safe to call during render
 * before a full-page redirect — marks the session sync, then POSTs without
 * awaiting so navigation can proceed.
 */
export function logFoodProfileExperimentExposures(): void {
  for (const experimentRef of FOOD_PROFILE_EXPOSURE_EXPERIMENT_REFS) {
    if (hasLoggedExposure(experimentRef)) continue;
    markLoggedExposure(experimentRef);

    void postExposure(experimentRef);
  }
}

async function postExposure(experimentRef: string): Promise<void> {
  try {
    const res = await postApiV2CustomerExperimentExposure(experimentRef);
    if (res.status < 200 || res.status >= 300) {
      logUnknownErrorResponse('Failed to log experiment exposure', {
        key: 'foodProfile.experimentExposure',
        experimentRef,
        error: JSON.stringify(res.data, null, 2),
      });
    }
  } catch (error: unknown) {
    logExceptionOnce(error, {
      key: 'foodProfile.experimentExposure.exception',
      experimentRef,
    });
  }
}

export function useFoodProfileExperimentExposure() {
  useEffect(() => {
    logFoodProfileExperimentExposures();
  }, []);
}

function hasLoggedExposure(experimentRef: string): boolean {
  if (loggedFoodProfileExperimentExposures.has(experimentRef)) return true;
  return readLoggedExposuresFromSession().has(experimentRef);
}

function markLoggedExposure(experimentRef: string): void {
  loggedFoodProfileExperimentExposures.add(experimentRef);

  const logged = readLoggedExposuresFromSession();
  if (logged.has(experimentRef)) return;
  logged.add(experimentRef);
  writeLoggedExposuresToSession(logged);
}

function readLoggedExposuresFromSession(): Set<string> {
  const storage = getSessionStorage();
  if (storage === null) return new Set();

  try {
    const raw = storage.getItem(SESSION_STORAGE_KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.filter((value): value is string => typeof value === 'string')
    );
  } catch {
    return new Set();
  }
}

function writeLoggedExposuresToSession(logged: Set<string>): void {
  const storage = getSessionStorage();
  if (storage === null) return;

  try {
    storage.setItem(SESSION_STORAGE_KEY, JSON.stringify([...logged]));
  } catch {
    // Private browsing / locked-down WebViews — in-memory set still dedupes.
  }
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Test-only: clear session + in-memory dedupe between specs. */
export function resetFoodProfileExperimentExposureForTests() {
  loggedFoodProfileExperimentExposures.clear();
  const storage = getSessionStorage();
  if (storage === null) return;
  try {
    storage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // ignore
  }
}
